import React, { useCallback, forwardRef, useImperativeHandle, useRef } from "react";
import { Editor, EditorProvider, JSONContent, useCurrentEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";

import { Bold, Italic, Link2, Link2Off, List, ListOrdered, RotateCcw, RotateCw, Strikethrough } from "lucide-react";

const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    // TextStyle.configure({ types: [ListItem.name] }),
    StarterKit.configure({
        bulletList: {
            keepMarks: true,
            keepAttributes: false,
        },
        orderedList: {
            keepMarks: true,
            keepAttributes: false,
        },
    }),
    Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        isAllowedUri: (url, ctx) => {
            try {
                // construct URL
                const parsedUrl = url.includes(":") ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`);

                // use default validation
                if (!ctx.defaultValidate(parsedUrl.href)) {
                    return false;
                }

                // disallowed protocols
                const disallowedProtocols = ["ftp", "file", "mailto"];
                const protocol = parsedUrl.protocol.replace(":", "");

                if (disallowedProtocols.includes(protocol)) {
                    return false;
                }

                // only allow protocols specified in ctx.protocols
                const allowedProtocols = ctx.protocols.map((p) => (typeof p === "string" ? p : p.scheme));

                if (!allowedProtocols.includes(protocol)) {
                    return false;
                }

                // disallowed domains
                const disallowedDomains = ["example-phishing.com", "malicious-site.net"];
                const domain = parsedUrl.hostname;

                if (disallowedDomains.includes(domain)) {
                    return false;
                }

                // all checks have passed
                return true;
            } catch {
                return false;
            }
        },
        shouldAutoLink: (url) => {
            try {
                // construct URL
                const parsedUrl = url.includes(":") ? new URL(url) : new URL(`https://${url}`);

                // only auto-link if the domain is not in the disallowed list
                const disallowedDomains = ["example-no-autolink.com", "another-no-autolink.com"];
                const domain = parsedUrl.hostname;

                return !disallowedDomains.includes(domain);
            } catch {
                return false;
            }
        },
    }),
];

export type TipTapEditorHandle = {
    clear: () => void;
    disable: () => void;
    enable: () => void;
};

type TextEditorProps = {
	id: string
    value?: string | JSONContent;
    onChange?: (content: string | JSONContent) => void;
    onGetPlainText?: (content: string) => void;
    outputFormat?: "html" | "json";
    editable?: boolean;
    showToolbar?: boolean;
    className?: string;
};

const TipTapEditor = forwardRef<TipTapEditorHandle, TextEditorProps>(({
    id,
    value,
    onChange,
    onGetPlainText,
    showToolbar = true,
    editable = true }, ref) => {
    const editorRef = useRef<Editor | null>(null);
	const content = value || "";

	const handleUpdate = (editor: Editor) => {
		if (onChange) {
			onChange(JSON.stringify(editor.getJSON(), null, 2))
		}

		if (onGetPlainText) {
			onGetPlainText(editor.getText())
		}
    };
    
    useImperativeHandle(ref, () => ({
        clear: () => {
          editorRef.current?.commands.clearContent();
        },
        getHTML: () => {
          return editorRef.current?.getHTML() || "";
        },
        disable: () => {
            return editorRef.current?.setEditable(false);
          },
          enable: () => {
            return editorRef.current?.setEditable(true);
          },
    }), []);
	
	return (
		<div data-testid="editor" className={`tiptap-editor ${editable ? "" : "is-readonly"}`}>
			<EditorProvider
                key={id}
				slotBefore={ showToolbar ? <Toolbar /> : null}
				extensions={extensions}
				content={content}
				onUpdate={({ editor }) => handleUpdate(editor)}
				editable={editable}
                editorContainerProps={{ className: "editor-container" }}
                onCreate={({ editor }) => {
                    editorRef.current = editor;
                }}
			></EditorProvider>
		</div>
	);
});

TipTapEditor.displayName = "TipTapEditor";

export default TipTapEditor;

type Level = 1 | 2 | 3 | 4 | 5 | 6;

const headingLevels: Level[] = [1, 2, 3, 4, 5, 6];

const Toolbar = () => {
    const { editor } = useCurrentEditor();

    const setLink = useCallback(() => {
        const previousUrl = editor?.getAttributes("link").href;
        const url = window.prompt("URL", previousUrl);

        // cancelled
        if (url === null) {
            return;
        }

        // empty
        if (url === "") {
            editor?.chain().focus().extendMarkRange("link").unsetLink().run();

            return;
        }

        // update link
        try {
            editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        } catch (e) {
            alert(e.message);
        }
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="control-group">
            <div className="button-group">
                <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().chain().focus().undo().run()}>
                    <RotateCcw />
                </button>
                <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().chain().focus().redo().run()}>
                    <RotateCw />
                </button>
            </div>

            <div className="button-group">
                <button onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive("paragraph") ? "is-active" : ""}>
                    Paragraph
                </button>
                {headingLevels.map((level) => (
                    <button key={level} onClick={() => editor.chain().focus().toggleHeading({ level }).run()} className={editor.isActive("heading", { level }) ? "is-active" : ""}>
                        H{level}
                    </button>
                ))}
            </div>

            <div className="button-group">
                <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive("bold") ? "is-active" : ""}>
                    <Bold />
                </button>
                <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive("italic") ? "is-active" : ""}>
                    <Italic />
                </button>
                <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive("strike") ? "is-active" : ""}>
                    <Strikethrough />
                </button>
            </div>

            <div className="button-group">
                <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive("bulletList") ? "is-active" : ""}>
                    <List />
                </button>
                <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive("orderedList") ? "is-active" : ""}>
                    <ListOrdered />
                </button>
            </div>

            <div className="button-group">
                <button onClick={setLink} className={editor.isActive("link") ? "is-active" : ""}>
                    <Link2 />
                </button>
                <button onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")}>
                    <Link2Off />
                </button>
            </div>
        </div>
    );
};
